/**
 * Spherical Ocean Component — v3
 *
 * Gerstner waves with domain warping, FBM noise, tangent-plane displacement.
 * Uses camera-relative rendering for planetary-scale precision.
 * Ghibli-style fragment shader with SSS, foam, and warm tonal mapping.
 */

import { useRef, useMemo, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// ============================================================================
// Wave directions — Fibonacci sphere, uniformly distributed in 3D
// ============================================================================

const NUM_WAVE_DIRS = 12;
const WAVE_DIRS: THREE.Vector3[] = [];
for (let i = 0; i < NUM_WAVE_DIRS; i++) {
  const y = 1 - (2.0 * i + 1.0) / NUM_WAVE_DIRS;
  const radiusAtY = Math.sqrt(1 - y * y);
  const theta = 2.39996323 * i;
  WAVE_DIRS.push(
    new THREE.Vector3(
      radiusAtY * Math.cos(theta),
      y,
      radiusAtY * Math.sin(theta),
    ).normalize(),
  );
}

// ============================================================================
// Cube-Sphere Geometry (unit normals, welded edges)
// ============================================================================

function createCubeSphereGeometry(resolution: number): THREE.BufferGeometry {
  const faces = [
    { right: [1, 0, 0], up: [0, 1, 0], forward: [0, 0, 1] },
    { right: [-1, 0, 0], up: [0, 1, 0], forward: [0, 0, -1] },
    { right: [0, 0, 1], up: [0, 1, 0], forward: [1, 0, 0] },
    { right: [0, 0, -1], up: [0, 1, 0], forward: [-1, 0, 0] },
    { right: [1, 0, 0], up: [0, 0, 1], forward: [0, 1, 0] },
    { right: [1, 0, 0], up: [0, 0, -1], forward: [0, -1, 0] },
  ];

  const posMap = new Map<string, number>();
  const posArray: number[] = [];
  const nrmArray: number[] = [];
  const idxArray: number[] = [];

  function addVertex(px: number, py: number, pz: number): number {
    const len = Math.sqrt(px * px + py * py + pz * pz);
    const nx = px / len;
    const ny = py / len;
    const nz = pz / len;
    const key = `${(nx * 1e8) | 0},${(ny * 1e8) | 0},${(nz * 1e8) | 0}`;
    const existing = posMap.get(key);
    if (existing !== undefined) return existing;
    const idx = posArray.length / 3;
    posMap.set(key, idx);
    posArray.push(nx, ny, nz);
    nrmArray.push(nx, ny, nz);
    return idx;
  }

  for (let f = 0; f < 6; f++) {
    const { right: r, up: u, forward: fw } = faces[f];
    const faceVerts: number[] = [];

    const cx = r[1] * u[2] - r[2] * u[1];
    const cy = r[2] * u[0] - r[0] * u[2];
    const cz = r[0] * u[1] - r[1] * u[0];
    const flip = fw[0] * cx + fw[1] * cy + fw[2] * cz > 0;

    for (let y = 0; y < resolution; y++) {
      for (let x = 0; x < resolution; x++) {
        const s = (x / (resolution - 1)) * 2 - 1;
        const t = (y / (resolution - 1)) * 2 - 1;
        const px = fw[0] + r[0] * s + u[0] * t;
        const py = fw[1] + r[1] * s + u[1] * t;
        const pz = fw[2] + r[2] * s + u[2] * t;
        faceVerts.push(addVertex(px, py, pz));
      }
    }

    for (let y = 0; y < resolution - 1; y++) {
      for (let x = 0; x < resolution - 1; x++) {
        const a = faceVerts[y * resolution + x];
        const b = faceVerts[y * resolution + x + 1];
        const c = faceVerts[(y + 1) * resolution + x];
        const d = faceVerts[(y + 1) * resolution + x + 1];
        if (flip) {
          idxArray.push(a, b, c, b, d, c);
        } else {
          idxArray.push(a, c, b, b, c, d);
        }
      }
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(posArray, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(nrmArray, 3));
  geometry.setIndex(idxArray);
  return geometry;
}

// ============================================================================
// Shared GLSL: simplex noise, fbm, attenuation, domain warp, wave displacement
// ============================================================================

const WAVE_GLSL = /* glsl */ `
  precision highp float;

  uniform float uTime;
  uniform float uR;
  uniform float uAmplitude;
  uniform float uWavelength;
  uniform float uSteepness;
  uniform float uSpeed;
  uniform int   uGerstnerLayers;
  uniform int   uNoiseOctaves;
  uniform float uNoiseStrength;
  uniform float uChop;
  uniform float uFalloff;
  uniform float uMinScale;
  uniform vec3  uIslands[8];
  uniform int   uNumIslands;
  uniform vec3  uWaveDirs[12];

  vec3 mod289v(vec3 x) { return x - floor(x * (1.0/289.0)) * 289.0; }
  vec4 mod289v4(vec4 x) { return x - floor(x * (1.0/289.0)) * 289.0; }
  vec4 perm(vec4 x) { return mod289v4(((x * 34.0) + 1.0) * x); }
  vec4 tis(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

  float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    i = mod289v(i);
    vec4 p = perm(perm(perm(
      i.z + vec4(0.0, i1.z, i2.z, 1.0))
      + i.y + vec4(0.0, i1.y, i2.y, 1.0))
      + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0) * 2.0 + 1.0;
    vec4 s1 = floor(b1) * 2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    vec4 norm = tis(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
  }

  float fbm(vec3 p, int oct) {
    float val = 0.0, amp = 0.5, freq = 1.0;
    for (int i = 0; i < 8; i++) {
      if (i >= oct) break;
      val += amp * snoise(p * freq);
      freq *= 2.17; amp *= 0.48;
      p += vec3(1.73, 9.21, 4.37);
    }
    return val;
  }

  float getAtten(vec3 pos) {
    float mn = 99999.0;
    for (int i = 0; i < 8; i++) {
      if (i >= uNumIslands) break;
      float d = acos(clamp(dot(normalize(pos), normalize(uIslands[i])), -1.0, 1.0)) * uR;
      mn = min(mn, d);
    }
    float t = clamp(mn / uFalloff, 0.0, 1.0);
    t = t * t * (3.0 - 2.0 * t);
    return uMinScale + (1.0 - uMinScale) * t;
  }

  mat3 rotX(float a) { float c=cos(a),s=sin(a); return mat3(1,0,0, 0,c,-s, 0,s,c); }
  mat3 rotY(float a) { float c=cos(a),s=sin(a); return mat3(c,0,s, 0,1,0, -s,0,c); }
  mat3 rotZ(float a) { float c=cos(a),s=sin(a); return mat3(c,-s,0, s,c,0, 0,0,1); }

  vec3 domainWarp(vec3 sn, float time) {
    vec3 n1 = vec3(
      snoise(sn * 1.8 + vec3(0.0, 0.0, time * 0.02)),
      snoise(sn * 1.8 + vec3(5.7, 2.3, time * 0.02)),
      snoise(sn * 1.8 + vec3(1.4, 8.1, time * 0.02))
    );
    vec3 n2 = vec3(
      snoise(sn * 3.6 + n1 * 0.5 + vec3(time * 0.015, 0.0, 0.0)),
      snoise(sn * 3.6 + n1 * 0.5 + vec3(3.1, 7.2, time * 0.015)),
      snoise(sn * 3.6 + n1 * 0.5 + vec3(6.8, 1.5, time * 0.015))
    );
    return sn + n1 * 0.12 + n2 * 0.06;
  }

  float heightAtWarped(vec3 worldPos, vec3 warpedSn, float atten, float time) {
    float h = 0.0;
    float baseFreq = 6.2831853 / uWavelength;
    for (int i = 0; i < 12; i++) {
      if (i >= uGerstnerLayers) break;
      float fi = float(i);
      float amp = uAmplitude * pow(0.62, fi) * atten;
      float freq = baseFreq * pow(1.68, fi);
      float phase = -time * sqrt(9.81 * freq) + fi * 1.374;
      float d = dot(warpedSn, uWaveDirs[i]) * uR;
      h += amp * sin(d * freq + phase);
    }
    return h;
  }

  vec3 waveDisplace(vec3 pos) {
    vec3 sn = normalize(pos);
    float atten = getAtten(pos);
    float time = uTime * uSpeed;
    float baseFreq = 6.2831853 / uWavelength;
    vec3 warpedSn = domainWarp(sn, time);

    vec3 totalDisp = vec3(0.0);
    for (int i = 0; i < 12; i++) {
      if (i >= uGerstnerLayers) break;
      float fi = float(i);
      float amp = uAmplitude * pow(0.62, fi) * atten;
      float freq = baseFreq * pow(1.68, fi);
      float steep = uSteepness * pow(0.85, fi);
      float phase = -time * sqrt(9.81 * freq) + fi * 1.374;
      vec3 dir = uWaveDirs[i];
      float d = dot(warpedSn, dir) * uR;
      float w = d * freq + phase;
      float s = sin(w); float c = cos(w);
      vec3 tangentDir = dir - sn * dot(dir, sn);
      float tangentLen = length(tangentDir);
      if (tangentLen > 0.001) tangentDir /= tangentLen;
      else tangentDir = vec3(0.0);
      float q = steep / (freq * amp + 0.001);
      totalDisp += tangentDir * q * amp * c * tangentLen + sn * amp * s;
    }
    float noiseDisp = 0.0;
    if (uNoiseOctaves > 0) {
      noiseDisp = fbm(rotY(time*0.042)*rotX(time*0.03)*(sn*3.5), uNoiseOctaves) * uNoiseStrength * atten;
      noiseDisp += snoise(rotZ(time*0.072)*rotY(time*0.048)*(sn*12.0)) * uNoiseStrength * 0.25 * atten;
    }
    float radialPart = dot(totalDisp, sn);
    vec3 tangentialPart = totalDisp - sn * radialPart;
    return sn * (radialPart + noiseDisp) + tangentialPart * uChop;
  }
`;

// ============================================================================
// Vertex Shader — Camera-Relative Gerstner + FBM on Sphere
// ============================================================================

const vertexShader = WAVE_GLSL + /* glsl */ `
  uniform vec3 uCameraDir;
  uniform float uCameraAlt;

  varying vec3 vWorldPos;
  varying vec3 vNormal;
  varying float vHeight;
  varying float vAtten;
  varying float vFoam;
  varying float vCrest;
  varying vec3 vSphereNormal;
  varying vec3 vCamRel;

  void main() {
    vec3 pos = position * uR;
    vec3 sn = normalize(pos);
    float atten = getAtten(pos);
    float time = uTime * uSpeed;
    float baseFreq = 6.2831853 / uWavelength;

    vec3 warpedSn = domainWarp(sn, time);
    vec3 totalDisp = vec3(0.0);
    float totalHeight = 0.0;
    float foam = 0.0;
    float crestAccum = 0.0;

    for (int i = 0; i < 12; i++) {
      if (i >= uGerstnerLayers) break;
      float fi = float(i);
      float amp = uAmplitude * pow(0.62, fi) * atten;
      float freq = baseFreq * pow(1.68, fi);
      float steep = uSteepness * pow(0.85, fi);
      float phase = -time * sqrt(9.81 * freq) + fi * 1.374;
      vec3 dir = uWaveDirs[i];
      float d = dot(warpedSn, dir) * uR;
      float w = d * freq + phase;
      float s = sin(w); float c = cos(w);
      vec3 tangentDir = dir - sn * dot(dir, sn);
      float tangentLen = length(tangentDir);
      if (tangentLen > 0.001) tangentDir /= tangentLen;
      else tangentDir = vec3(0.0);
      float q = steep / (freq * amp + 0.001);
      totalDisp += tangentDir * q * amp * c * tangentLen + sn * amp * s;
      totalHeight += amp * s;
      foam += steep * freq * amp * c;
      crestAccum += amp * s;
    }

    float noiseDisp = 0.0;
    if (uNoiseOctaves > 0) {
      noiseDisp = fbm(rotY(time*0.042)*rotX(time*0.03)*(sn*3.5), uNoiseOctaves) * uNoiseStrength * atten;
      noiseDisp += snoise(rotZ(time*0.072)*rotY(time*0.048)*(sn*12.0)) * uNoiseStrength * 0.25 * atten;
    }

    float radialPart = dot(totalDisp, sn);
    vec3 tangentialPart = totalDisp - sn * radialPart;
    vec3 finalPos = pos + sn * (radialPart + noiseDisp) + tangentialPart * uChop;

    // Normal estimation via central differences
    vec3 up = vec3(0.0, 1.0, 0.0);
    vec3 T = cross(up, sn); if (length(T) < 0.001) T = cross(vec3(1,0,0), sn);
    T = normalize(T); vec3 B = normalize(cross(sn, T));
    float eps = 0.8;
    vec3 pR = normalize(pos + T * eps) * uR; vec3 pL = normalize(pos - T * eps) * uR;
    vec3 pF = normalize(pos + B * eps) * uR; vec3 pBk = normalize(pos - B * eps) * uR;
    vec3 wR = domainWarp(normalize(pR), time); vec3 wL = domainWarp(normalize(pL), time);
    vec3 wF = domainWarp(normalize(pF), time); vec3 wBk = domainWarp(normalize(pBk), time);
    float hR = heightAtWarped(pR, wR, atten, time); float hL = heightAtWarped(pL, wL, atten, time);
    float hF = heightAtWarped(pF, wF, atten, time); float hBk = heightAtWarped(pBk, wBk, atten, time);
    if (uNoiseOctaves > 0) {
      vec3 snR = normalize(pR); vec3 snL = normalize(pL);
      vec3 snF = normalize(pF); vec3 snBk2 = normalize(pBk);
      float ns = uNoiseStrength * atten;
      hR += fbm(rotY(time*0.042)*rotX(time*0.03)*(snR*3.5), uNoiseOctaves)*ns + snoise(rotZ(time*0.072)*rotY(time*0.048)*(snR*12.0))*ns*0.25;
      hL += fbm(rotY(time*0.042)*rotX(time*0.03)*(snL*3.5), uNoiseOctaves)*ns + snoise(rotZ(time*0.072)*rotY(time*0.048)*(snL*12.0))*ns*0.25;
      hF += fbm(rotY(time*0.042)*rotX(time*0.03)*(snF*3.5), uNoiseOctaves)*ns + snoise(rotZ(time*0.072)*rotY(time*0.048)*(snF*12.0))*ns*0.25;
      hBk += fbm(rotY(time*0.042)*rotX(time*0.03)*(snBk2*3.5), uNoiseOctaves)*ns + snoise(rotZ(time*0.072)*rotY(time*0.048)*(snBk2*12.0))*ns*0.25;
    }
    vec3 localN = normalize(vec3(hL - hR, 2.0 * eps, hBk - hF));
    vec3 worldN = normalize(T * localN.x + sn * localN.y + B * localN.z);

    vWorldPos = finalPos;
    vNormal = worldN;
    vHeight = totalHeight + noiseDisp;
    vAtten = atten;
    vSphereNormal = sn;
    float foamJ = clamp(-foam + 0.3, 0.0, 1.0);
    float crestF = clamp(crestAccum / (uAmplitude + 0.001) - 0.3, 0.0, 1.0);
    vFoam = (foamJ * 0.6 + crestF * 0.4) * atten;
    vCrest = clamp(crestAccum / (uAmplitude * 0.5 + 0.001), -1.0, 1.0);

    // Camera-relative position
    vec3 camRel = finalPos - uCameraDir * (uR + uCameraAlt);
    vCamRel = camRel;

    mat3 viewRot = mat3(modelViewMatrix);
    gl_Position = projectionMatrix * vec4(viewRot * camRel, 1.0);
  }
`;

// ============================================================================
// Fragment Shader — Ghibli-style ocean surface
// ============================================================================

const fragmentShader = /* glsl */ `
  precision highp float;

  uniform vec3 uLightDir;
  uniform float uSunHeight;
  uniform float uFoamIntensity;
  uniform float uSSSStrength;
  uniform float uR;
  uniform float uTime;

  varying vec3 vWorldPos;
  varying vec3 vNormal;
  varying float vHeight;
  varying float vAtten;
  varying float vFoam;
  varying float vCrest;
  varying vec3 vSphereNormal;
  varying vec3 vCamRel;

  vec3 mod289v(vec3 x) { return x - floor(x * (1.0/289.0)) * 289.0; }
  vec4 mod289v4(vec4 x) { return x - floor(x * (1.0/289.0)) * 289.0; }
  vec4 perm(vec4 x) { return mod289v4(((x * 34.0) + 1.0) * x); }
  vec4 tis(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
  float snoise(vec3 v) {
    const vec2 C = vec2(1.0/6.0, 1.0/3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i = floor(v + dot(v, C.yyy)); vec3 x0 = v - i + dot(i, C.xxx);
    vec3 g = step(x0.yzx, x0.xyz); vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy); vec3 i2 = max(g.xyz, l.zxy);
    vec3 x1 = x0 - i1 + C.xxx; vec3 x2 = x0 - i2 + C.yyy; vec3 x3 = x0 - D.yyy;
    i = mod289v(i);
    vec4 p = perm(perm(perm(
      i.z + vec4(0.0, i1.z, i2.z, 1.0))
      + i.y + vec4(0.0, i1.y, i2.y, 1.0))
      + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    float n_ = 0.142857142857; vec3 ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z); vec4 y_ = floor(j - 7.0 * x_);
    vec4 x = x_ * ns.x + ns.yyyy; vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4(x.xy, y.xy); vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0) * 2.0 + 1.0; vec4 s1 = floor(b1) * 2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy; vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
    vec3 p0 = vec3(a0.xy, h.x); vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z); vec3 p3 = vec3(a1.zw, h.w);
    vec4 norm = tis(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
  }
  mat3 rotY(float a) { float c=cos(a),s=sin(a); return mat3(c,0,s, 0,1,0, -s,0,c); }
  mat3 rotZ(float a) { float c=cos(a),s=sin(a); return mat3(c,-s,0, s,c,0, 0,0,1); }

  void main() {
    vec3 N = normalize(vNormal);
    vec3 V = normalize(-vCamRel);
    vec3 L = normalize(uLightDir);
    vec3 H = normalize(L + V);

    // Day/night factor (matches utils/lighting.ts)
    float sunIntensity = smoothstep(-0.05, 0.3, uSunHeight);
    float sunsetFactor = smoothstep(-0.05, 0.1, uSunHeight)
                       * (1.0 - smoothstep(0.1, 0.3, uSunHeight));

    // Sun color: warm at sunset, white at day, zero at night
    vec3 sunColor = mix(vec3(1.0, 0.98, 0.95), vec3(1.0, 0.6, 0.3), sunsetFactor);
    sunColor *= sunIntensity;

    // Bright turquoise palette — day vs night variants
    vec3 deepShadow   = mix(vec3(0.02, 0.04, 0.08), vec3(0.18, 0.42, 0.55), sunIntensity);
    vec3 bodyBlue     = mix(vec3(0.03, 0.08, 0.14), vec3(0.30, 0.68, 0.82), sunIntensity);
    vec3 midTeal      = mix(vec3(0.04, 0.10, 0.16), vec3(0.40, 0.80, 0.90), sunIntensity);
    vec3 shallowGreen = mix(vec3(0.05, 0.12, 0.16), vec3(0.50, 0.88, 0.85), sunIntensity);

    float slopeAmount = 1.0 - max(dot(N, vSphereNormal), 0.0);
    float facing = max(dot(N, V), 0.0);

    vec3 waterColor = mix(deepShadow, bodyBlue, smoothstep(0.0, 0.15, slopeAmount));
    waterColor = mix(waterColor, midTeal, smoothstep(0.1, 0.45, slopeAmount) * 0.7);
    waterColor = mix(waterColor, shallowGreen, smoothstep(0.3, 0.75, slopeAmount) * 0.4);

    // Near islands: shallow turquoise
    vec3 shallowIsland = mix(vec3(0.02, 0.05, 0.08), vec3(0.25, 0.55, 0.58), sunIntensity);
    waterColor = mix(shallowIsland, waterColor, vAtten);

    // Diffuse
    float NdL = dot(N, L);
    float wrap = NdL * 0.35 + 0.65;
    float diff = wrap * wrap;
    vec3 shadowTint = vec3(0.02, 0.03, 0.05);
    vec3 diffuse = mix(shadowTint, waterColor * 1.4, diff);

    // Fresnel — sky reflection dims at night
    float NdV = max(dot(N, V), 0.0);
    float fresnel = pow(1.0 - NdV, 3.5);
    vec3 skyWarm   = mix(vec3(0.02, 0.04, 0.08), vec3(0.28, 0.50, 0.56), sunIntensity);
    vec3 horizWarm = mix(vec3(0.03, 0.05, 0.10), vec3(0.38, 0.60, 0.63), sunIntensity);
    vec3 reflectColor = mix(skyWarm, horizWarm, fresnel);

    // Moonlight — faint blue ambient at night
    float moonFactor = (1.0 - sunIntensity) * 0.08;
    vec3 moonColor = vec3(0.15, 0.20, 0.35) * moonFactor;

    // Specular — warm golden, scales with sun
    float spec1 = pow(max(dot(N, H), 0.0), 128.0) * 0.8 * sunIntensity;
    float spec2 = pow(max(dot(N, H), 0.0), 16.0) * 0.06 * sunIntensity;
    vec3 specular = (spec1 + spec2) * sunColor;

    // SSS — scales with sun
    vec3 sssDir = normalize(L + N * 0.5);
    float sssDot = pow(clamp(dot(V, -sssDir), 0.0, 1.0), 2.0);
    float slopeToLight = clamp(NdL + 0.4, 0.0, 1.0);
    vec3 sssColor = vec3(0.10, 0.38, 0.30) * sssDot * slopeToLight * slopeAmount * uSSSStrength * 3.0 * sunIntensity;

    // Foam
    float foamMask = vFoam * uFoamIntensity;
    vec3 foamP = rotY(uTime * 0.08) * rotZ(uTime * 0.05) * (vSphereNormal * 5.0);
    float fn1 = snoise(foamP) * 0.5 + 0.5;
    vec3 foamP2 = rotZ(uTime * 0.06) * rotY(uTime * 0.04) * (vSphereNormal * 14.0);
    fn1 *= snoise(foamP2) * 0.4 + 0.6;
    foamMask *= fn1;
    foamMask = smoothstep(0.2, 0.55, foamMask);
    vec3 foamColor = mix(vec3(0.15, 0.18, 0.22), vec3(0.85, 0.90, 0.87), sunIntensity) * (0.85 + 0.15 * fn1);
    foamColor *= (diff * 0.3 + 0.7);

    // Soft crest highlight
    float crestEdge = smoothstep(0.35, 0.7, slopeAmount) * smoothstep(0.25, 0.5, facing);
    float edgeNoise = snoise(vSphereNormal * 20.0 + vec3(uTime * 0.025)) * 0.5 + 0.5;
    float edgeFoam = crestEdge * edgeNoise * 0.3 * sunIntensity;
    vec3 edgeColor = mix(vec3(0.10, 0.12, 0.15), vec3(0.75, 0.82, 0.80), sunIntensity);

    // Compose
    vec3 color = diffuse + moonColor;
    color = mix(color, reflectColor, fresnel * 0.35);
    color += specular;
    color += sssColor * vAtten;
    color = mix(color, foamColor, foamMask);
    color = mix(color, edgeColor, edgeFoam);

    // Tone mapping — bright and open
    color = color / (color + vec3(1.0));
    color = pow(color, vec3(1.0 / 2.0));

    // Gentle desaturation
    float luma = dot(color, vec3(0.299, 0.587, 0.114));
    color = mix(vec3(luma), color, 0.95);
    color += vec3(0.012, 0.018, 0.014) * sunIntensity;

    gl_FragColor = vec4(color, 1.0);
  }
`;

// ============================================================================
// Exported WAVE_GLSL for wake shader reuse
// ============================================================================

export { WAVE_GLSL as OCEAN_WAVE_GLSL };

// ============================================================================
// React Three Fiber Component
// ============================================================================

export interface IslandDampingData {
  position: THREE.Vector3;
  angularRadius: number;
}

export interface SphericalOceanProps {
  radius?: number;
  resolution?: number;
  islands?: IslandDampingData[];
  sunPosition?: THREE.Vector3;
}

// Default wave parameters (tuned for RADIUS=10000)
const DEFAULT_AMPLITUDE = 3.0;
const DEFAULT_WAVELENGTH = 90.0;
const DEFAULT_STEEPNESS = 0.0;
const DEFAULT_SPEED = 0.3;
const DEFAULT_GERSTNER_LAYERS = 1;
const DEFAULT_NOISE_OCTAVES = 3;
const DEFAULT_NOISE_STRENGTH = 2.9;
const DEFAULT_CHOP = 1.0;
const DEFAULT_FALLOFF = 3000.0;
const DEFAULT_FOAM_INTENSITY = 0.15;
const DEFAULT_SSS_STRENGTH = 0.5;

export function SphericalOcean({
  radius = 2000,
  resolution = 128,
  islands = [],
  sunPosition,
}: SphericalOceanProps) {
  const meshRef = useRef<THREE.Mesh>(null);

  const falloff = DEFAULT_FALLOFF;
  const minScale = 0.04;

  const geometry = useMemo(
    () => createCubeSphereGeometry(resolution),
    [resolution],
  );

  const islandCenters = useMemo(() => {
    const centers = new Array(8).fill(null).map(() => new THREE.Vector3());
    islands.forEach((isl, i) => {
      if (i < 8) centers[i].copy(isl.position);
    });
    return centers;
  }, [islands]);

  const islandCount = Math.min(islands.length, 8);

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uR: { value: radius },
      uAmplitude: { value: DEFAULT_AMPLITUDE },
      uWavelength: { value: DEFAULT_WAVELENGTH },
      uSteepness: { value: DEFAULT_STEEPNESS },
      uSpeed: { value: DEFAULT_SPEED },
      uGerstnerLayers: { value: DEFAULT_GERSTNER_LAYERS },
      uNoiseOctaves: { value: DEFAULT_NOISE_OCTAVES },
      uNoiseStrength: { value: DEFAULT_NOISE_STRENGTH },
      uChop: { value: DEFAULT_CHOP },
      uFalloff: { value: falloff },
      uMinScale: { value: minScale },
      uIslands: { value: islandCenters },
      uNumIslands: { value: islandCount },
      uWaveDirs: { value: WAVE_DIRS },
      uLightDir: { value: new THREE.Vector3(0.5, 0.3, 0.8).normalize() },
      uSunHeight: { value: 0.3 },
      uFoamIntensity: { value: DEFAULT_FOAM_INTENSITY },
      uSSSStrength: { value: DEFAULT_SSS_STRENGTH },
      uCameraDir: { value: new THREE.Vector3(0, 1, 0) },
      uCameraAlt: { value: 0 },
    }),
    [radius, falloff, islandCenters, islandCount],
  );

  const material = useMemo(() => {
    return new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms,
      transparent: false,
      side: THREE.FrontSide,
      depthWrite: true,
    });
  }, [uniforms]);

  useEffect(() => {
    return () => {
      geometry.dispose();
      material.dispose();
    };
  }, [geometry, material]);

  useFrame((state) => {
    uniforms.uTime.value = state.clock.elapsedTime;

    const camPos = state.camera.position;
    const camLen = camPos.length();
    if (camLen > 0) {
      uniforms.uCameraDir.value.set(
        camPos.x / camLen,
        camPos.y / camLen,
        camPos.z / camLen,
      );
      uniforms.uCameraAlt.value = camLen - radius;
    }

    if (sunPosition) {
      uniforms.uLightDir.value.copy(sunPosition).normalize();
      // Sun height = dot(sunDir, cameraDir) — same convention as other shaders
      const sd = uniforms.uLightDir.value;
      const cd = uniforms.uCameraDir.value;
      uniforms.uSunHeight.value = sd.x * cd.x + sd.y * cd.y + sd.z * cd.z;
    }
  });

  return (
    <mesh ref={meshRef} geometry={geometry} frustumCulled={false}>
      <primitive object={material} attach="material" />
    </mesh>
  );
}

export { SphericalOcean as Ocean };

// Export wave parameters + directions for CPU-side matching
export {
  WAVE_DIRS,
  DEFAULT_AMPLITUDE,
  DEFAULT_WAVELENGTH,
  DEFAULT_STEEPNESS,
  DEFAULT_SPEED,
  DEFAULT_GERSTNER_LAYERS,
  DEFAULT_NOISE_OCTAVES,
  DEFAULT_NOISE_STRENGTH,
  DEFAULT_CHOP,
};
